import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  TextField,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  Event as EventIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import careerEventsService from '../../services/careerEventsService';
import CareerEventDialog from '../CareerEvents/CareerEventDialog';
import { usePermissions } from '../PermissionsContext';

const UpcomingEventsWidget = ({ days = 365, limit = 100 }) => {
  // CEV-S1 — HR and the board (CareerEvents.View) see everybody's upcoming events;
  // everybody else sees only their own events that are marked visible to them.
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canViewAll = hasPermission('CareerEvents.View');
  const canManage = hasPermission('CareerEvents.Manage');
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Filter & Pagination state
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsData, typesData] = await Promise.all([
        canViewAll ? careerEventsService.getUpcomingEvents(days, limit) : loadMyUpcoming(),
        careerEventsService.getEventTypes()
      ]);
      setAllEvents(eventsData || []);
      setEventTypes(typesData || []);
    } catch (err) {
      console.error('Failed to load upcoming events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // CEV-S1 — own events only: Scheduled, from today up to `days` ahead, soonest first
  const loadMyUpcoming = async () => {
    const mine = (await careerEventsService.getMyEvents()) || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setDate(end.getDate() + days);
    return mine
      .filter(e => e.status === 'Scheduled')
      .filter(e => {
        const d = new Date(e.eventDate);
        d.setHours(0, 0, 0, 0);
        return d >= today && d <= end;
      })
      .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
      .slice(0, limit);
  };

  useEffect(() => {
    if (permissionsLoading) return;   // CEV-S1 — wait for the permissions before choosing the source
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, limit, canViewAll, permissionsLoading]);

  // Filter events by type
  const filteredEvents = useMemo(() => {
    if (!filterType) return allEvents;
    return allEvents.filter(e => e.eventTypeId === filterType || e.eventTypeName === filterType);
  }, [allEvents, filterType]);

  // Paginated events
  const paginatedEvents = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEvents.slice(start, start + rowsPerPage);
  }, [filteredEvents, page, rowsPerPage]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [filterType]);

  const handleEventClick = (event) => {
    if (!canManage) return;   // CEV-S1 — only HR and the board open the edit dialog
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleDialogClose = (saved) => {
    setDialogOpen(false);
    setSelectedEvent(null);
    if (saved) {
      loadEvents(); // Refresh the list if event was edited
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('bg-BG', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getDaysChipColor = (days) => {
    if (days <= 0) return 'error';
    if (days <= 7) return 'warning';
    if (days <= 14) return 'info';
    return 'success';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const urgentCount = filteredEvents.filter(e => getDaysUntil(e.eventDate) <= 7).length;
  const upcomingCount = filteredEvents.filter(e => getDaysUntil(e.eventDate) > 7 && getDaysUntil(e.eventDate) <= 14).length;

  // Get unique event types from loaded types
  const typeOptions = useMemo(() => {
    return eventTypes.map(t => ({ id: t.id, name: t.name }));
  }, [eventTypes]);

  return (
    <Card className="combo-card" sx={{ borderLeft: urgentCount > 0 ? '4px solid #f57c00' : '4px solid #4caf50', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: urgentCount > 0 ? 'warning.main' : 'success.main', width: 40, height: 40 }}>
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" className="combo-title" sx={{ lineHeight: 1.2 }}>
                {canViewAll ? 'Upcoming Events' : 'My Upcoming Events'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Next 1 year • {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {urgentCount > 0 && (
              <Chip
                label={`${urgentCount} urgent`}
                size="small"
                color="warning"
                sx={{ fontWeight: 600 }}
              />
            )}
            {upcomingCount > 0 && (
              <Chip
                label={`${upcomingCount} soon`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadEvents} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filter by event type */}
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Filter by Type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">{error}</Typography>
        ) : filteredEvents.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body1">
              {filterType ? 'No events matching the selected filter' : 'No upcoming events in the next year'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TableContainer sx={{ flex: 1, maxHeight: 350 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Date</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'background.paper', minWidth: 90 }}>Days Left</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEvents.map((event) => {
                    const daysUntil = getDaysUntil(event.eventDate);
                    const isUrgent = daysUntil <= 7;
                    return (
                      <TableRow
                        key={event.id}
                        hover
                        onClick={() => handleEventClick(event)}
                        sx={{
                          cursor: canManage ? 'pointer' : 'default',
                          bgcolor: isUrgent ? 'rgba(255, 152, 0, 0.04)' : 'inherit',
                          '&:hover': {
                            bgcolor: isUrgent ? 'rgba(255, 152, 0, 0.08)' : undefined
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light' }}>
                              {getInitials(event.employeeName)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: isUrgent ? 600 : 400 }}>
                              {event.employeeName || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={event.title || ''} arrow>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {event.eventTypeName || event.title || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(event.eventDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                            size="small"
                            color={getDaysChipColor(daysUntil)}
                            sx={{
                              fontWeight: isUrgent ? 700 : 500,
                              minWidth: 70
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredEvents.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows:"
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </Box>
        )}
      </CardContent>

      {/* Career Event Edit Dialog */}
      <CareerEventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        event={selectedEvent}
        employeeId={selectedEvent?.employeeId}
        personName={selectedEvent?.employeeName}
        eventTypes={eventTypes}
        viewMode={false}
      />
    </Card>
  );
};

export default UpcomingEventsWidget;

